import { useQuery } from '@tanstack/react-query';
import { UserGroupIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { teamApi } from '../../lib/api';

/**
 * FDIC BankFind peer discovery panel (Michael Wallace, 2026-06-04): given a bank's FDIC cert,
 * show the N most asset-comparable active institutions — Clarid determines the peer group by
 * logic (asset proximity + geography) rather than asking the bank to guess. Reusable across
 * Peer Benchmarking, a standalone tool, and the CRA Appendix peer tables.
 */

interface Peer {
  cert: number;
  name: string | null;
  city: string | null;
  state: string | null;
  assetsInThousands: number | null;
  primaryRegulator: string | null;
  charterType: string | null;
  concentration?: { primary: string } | null;
}
interface FindPeersResponse {
  subject: { cert: number; name: string | null; state: string | null; assetsInThousands: number | null };
  peers: Peer[];
  subjectConcentration?: { primary: string } | null;
  criteria: { state: string | null; assetMinThousands: number; assetMaxThousands: number; broadenedToNationwide: boolean; concentrationWeighted: boolean };
  note?: string;
}

const usd = (thousands: number | null) => {
  if (thousands == null) return '—';
  const dollars = thousands * 1000;
  return dollars >= 1_000_000_000
    ? `$${(dollars / 1_000_000_000).toFixed(2)}B`
    : `$${(dollars / 1_000_000).toFixed(0)}M`;
};

export default function FindPeersPanel({ cert, count = 5 }: { cert: number; count?: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['fdic-peers', cert, count],
    queryFn: async () => (await teamApi.findPeers(cert, count)).data as FindPeersResponse,
    enabled: !!cert,
    staleTime: 1000 * 60 * 60, // peers change slowly — cache an hour
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-1">
        <UserGroupIcon className="h-5 w-5 text-teal-600" />
        <h3 className="text-base font-bold text-gray-900">CRA Peer Group</h3>
        <span className="ml-2 text-xs text-gray-400">FDIC BankFind · asset-comparable, active</span>
      </div>
      <p className="mb-3 text-xs text-gray-500">
        Your named, asset-comparable institutions (FDIC BankFind). This is the <span className="font-medium">CRA</span> peer
        group — the same one used in CRA Appendix Table 2 and the Large Bank reports. It is distinct from the HMDA market
        peers on the “HMDA Metrics” tab, which compare fair-lending denial rates and market share against all lenders in
        your assessment areas.
      </p>

      {isLoading && <p className="py-4 text-sm text-gray-400">Finding comparable peers…</p>}

      {isError && (
        <div className="flex items-center gap-2 py-4 text-sm text-amber-700">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
          Couldn’t reach FDIC BankFind right now. Try again shortly.
        </div>
      )}

      {data && data.note && (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />
          {data.note}
        </div>
      )}

      {data && !data.note && (
        <>
          <p className="mb-3 text-xs text-gray-500">
            {data.peers.length} peer{data.peers.length === 1 ? '' : 's'} for{' '}
            <span className="font-medium text-gray-700">{data.subject.name ?? `cert #${data.subject.cert}`}</span>{' '}
            ({usd(data.subject.assetsInThousands)}) ·{' '}
            {data.criteria.broadenedToNationwide
              ? 'nationwide'
              : data.criteria.state ?? 'nationwide'}{' '}
            · {usd(data.criteria.assetMinThousands)}–{usd(data.criteria.assetMaxThousands)} assets
            {data.criteria.broadenedToNationwide && ' (widened — too few in-state)'}
            {data.subjectConcentration && (
              <> · portfolio: <span className="font-medium text-gray-700">{data.subjectConcentration.primary}</span></>
            )}
          </p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
              <tr>
                <th className="px-2 py-2 text-left font-medium">Institution</th>
                <th className="px-2 py-2 text-left font-medium">Location</th>
                <th className="px-2 py-2 text-right font-medium">Total Assets</th>
                <th className="px-2 py-2 text-left font-medium">Portfolio Mix</th>
                <th className="px-2 py-2 text-left font-medium">Regulator</th>
                <th className="px-2 py-2 text-right font-medium">FDIC Cert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.peers.map((p) => (
                <tr key={p.cert}>
                  <td className="px-2 py-2 text-gray-800">{p.name ?? '—'}</td>
                  <td className="px-2 py-2 text-gray-500">
                    {[p.city, p.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{usd(p.assetsInThousands)}</td>
                  <td className="px-2 py-2 text-gray-500">{p.concentration?.primary ?? '—'}</td>
                  <td className="px-2 py-2 text-gray-500">{p.primaryRegulator ?? '—'}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-400">#{p.cert}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-gray-400">
            Peers are active institutions matched on total assets {data.criteria.concentrationWeighted ? 'and loan-portfolio mix' : ''} (within the band above),
            same state where possible. Source: FDIC BankFind.
          </p>
        </>
      )}
    </div>
  );
}
